import React, { useState, useEffect } from 'react';
import { PriceAdjustment } from './types';

interface PriceAdjustmentSectionProps {
  originalTotal: number;
  currentAdjustment?: PriceAdjustment | null;
  userRole: 'admin' | 'crew';
  maxDiscountPercent?: number; // For crew: 80
  onApplyAdjustment: (
    adjustedTotal: number,
    discountPercent: number | null,
    reason: string
  ) => Promise<void>;
  isSubmitting?: boolean;
}

const PriceAdjustmentSection: React.FC<PriceAdjustmentSectionProps> = ({
  originalTotal,
  currentAdjustment,
  userRole,
  maxDiscountPercent = 100, // Admin has no limit by default
  onApplyAdjustment,
  isSubmitting = false,
}) => {
  const [adjustmentMode, setAdjustmentMode] = useState<'percentage' | 'custom'>('percentage');
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate minimum allowed price for crew based on max discount percent
  // If max discount is 20%, minimum price is 80% of original
  const minAllowedPrice = userRole === 'crew' ? originalTotal * (1 - maxDiscountPercent / 100) : 0;
  const maxAllowedDiscount = userRole === 'crew' ? maxDiscountPercent : 100;

  // Initialize from existing adjustment
  useEffect(() => {
    if (currentAdjustment) {
      setIsExpanded(true);
      setReason(currentAdjustment.adjustment_reason);
      if (currentAdjustment.discount_percent !== null) {
        setAdjustmentMode('percentage');
        setDiscountPercent(currentAdjustment.discount_percent);
      } else {
        setAdjustmentMode('custom');
        setCustomAmount(currentAdjustment.adjusted_total.toFixed(2));
      }
    }
  }, [currentAdjustment]);

  // Calculate new total based on mode
  const calculateNewTotal = (): number => {
    if (adjustmentMode === 'percentage') {
      return originalTotal * (1 - discountPercent / 100);
    } else {
      const parsed = parseFloat(customAmount);
      return isNaN(parsed) ? originalTotal : parsed;
    }
  };

  const newTotal = calculateNewTotal();
  const discountAmount = originalTotal - newTotal;

  // Validate the adjustment
  const validateAdjustment = (): { valid: boolean; error?: string } => {
    if (newTotal < 0) {
      return { valid: false, error: 'Price cannot be negative' };
    }

    if (userRole === 'crew' && newTotal < minAllowedPrice) {
      return {
        valid: false,
        error: `As crew, you cannot discount more than ${maxDiscountPercent}%. Minimum price: $${minAllowedPrice.toFixed(2)}`,
      };
    }

    if (!reason.trim()) {
      return { valid: false, error: 'Please provide a reason for the adjustment' };
    }

    return { valid: true };
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccessMessage(null);
    const validation = validateAdjustment();
    if (!validation.valid) {
      setError(validation.error || 'Invalid adjustment');
      return;
    }

    try {
      const finalDiscountPercent = adjustmentMode === 'percentage' ? discountPercent : null;
      await onApplyAdjustment(newTotal, finalDiscountPercent, reason.trim());
      // Show success message
      setSuccessMessage('Price adjustment applied successfully!');
      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to apply adjustment');
    }
  };

  const handleRemoveAdjustment = async () => {
    setError(null);
    try {
      // Reset to original price
      await onApplyAdjustment(originalTotal, 0, 'Adjustment removed');
      setDiscountPercent(0);
      setCustomAmount('');
      setReason('');
      setIsExpanded(false);
    } catch (err: any) {
      setError(err.message || 'Failed to remove adjustment');
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium mt-3"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Adjust Price
      </button>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900 flex items-center">
          <span className="mr-2">💰</span>
          Price Adjustment
        </h4>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Role-based warning for crew */}
      {userRole === 'crew' && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
          <span className="font-medium">⚠️ Crew Limit:</span> You can discount up to {maxAllowedDiscount}% (minimum ${minAllowedPrice.toFixed(2)})
        </div>
      )}

      {/* Adjustment Mode Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Adjustment Type</label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="adjustmentMode"
              value="percentage"
              checked={adjustmentMode === 'percentage'}
              onChange={() => setAdjustmentMode('percentage')}
              className="mr-2"
            />
            <span className="text-sm">Percentage Discount</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="adjustmentMode"
              value="custom"
              checked={adjustmentMode === 'custom'}
              onChange={() => setAdjustmentMode('custom')}
              className="mr-2"
            />
            <span className="text-sm">Custom Amount</span>
          </label>
        </div>
      </div>

      {/* Percentage Discount Input */}
      {adjustmentMode === 'percentage' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Discount: {discountPercent}% (-${discountAmount.toFixed(2)})
          </label>
          <input
            type="range"
            min="0"
            max={maxAllowedDiscount}
            value={discountPercent}
            onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0%</span>
            <span>{maxAllowedDiscount}%</span>
          </div>
          {/* Quick percentage buttons */}
          <div className="flex space-x-2 mt-2">
            {[10, 20, 30, 50].filter(p => p <= maxAllowedDiscount).map((percent) => (
              <button
                key={percent}
                type="button"
                onClick={() => setDiscountPercent(percent)}
                className={`px-3 py-1 text-sm rounded ${
                  discountPercent === percent
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Amount Input */}
      {adjustmentMode === 'custom' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Total Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
            <input
              type="number"
              min={minAllowedPrice}
              max={originalTotal}
              step="0.01"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder={originalTotal.toFixed(2)}
              className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {userRole === 'crew' && (
            <p className="text-xs text-gray-500 mt-1">
              Minimum: ${minAllowedPrice.toFixed(2)} | Original: ${originalTotal.toFixed(2)}
            </p>
          )}
        </div>
      )}

      {/* Reason Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Friend discount - approved by owner"
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Price Summary */}
      <div className="mb-4 p-3 bg-white border border-gray-200 rounded-md">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Original Total:</span>
          <span>${originalTotal.toFixed(2)}</span>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm text-green-600 mb-1">
            <span>Discount:</span>
            <span>-${discountAmount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t">
          <span>New Total:</span>
          <span className={newTotal < originalTotal ? 'text-green-600' : ''}>
            ${newTotal.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm flex items-center">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !reason.trim()}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Applying...
            </>
          ) : currentAdjustment ? (
            'Update Adjustment'
          ) : (
            'Apply Adjustment'
          )}
        </button>
        {currentAdjustment && (
          <button
            onClick={handleRemoveAdjustment}
            disabled={isSubmitting}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            Remove
          </button>
        )}
      </div>

      {/* Existing Adjustment Info */}
      {currentAdjustment && (
        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
          Last adjusted by {currentAdjustment.adjusted_by_role} on{' '}
          {new Date(currentAdjustment.created_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default PriceAdjustmentSection;
